import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AdminService } from 'src/app/services/admin-service';
import { EditorialIntelligenceService } from 'src/app/services/editorial-intelligence-service';
import {
  DossierSource,
  EditorialDecision,
  EditorialIntelligenceDossier,
  IntelClaim,
  IntelMedia,
  IntelSource,
  IntelTimelineEvent,
  NextAction,
  OutstandingTask,
  ReadinessCategory,
} from 'src/app/models/editorial/editorialIntelligence';

interface IntelTab {
  key: string;
  label: string;
}

@Component({
  selector: 'pla-admin-editorial-intelligence',
  templateUrl: './admin-editorial-intelligence.component.html',
  styleUrls: ['./admin-editorial-intelligence.component.scss'],
})
export class AdminEditorialIntelligenceComponent implements OnInit, OnDestroy {
  public verified = false; // admin check passed
  public loading = true;
  public notAvailable = false; // registration has no dossier (mock or api)

  public registration = '';
  public dossier: EditorialIntelligenceDossier | null = null;
  public dataSource: DossierSource | null = null;

  public activeTab = 'summary';
  // Tab keys are unchanged (routing/switch untouched). Only the 'gaps' LABEL is
  // updated to "Outstanding Tasks" per the V2 brief — the tab itself remains.
  public readonly tabs: IntelTab[] = [
    { key: 'summary', label: 'Intelligence Summary' },
    { key: 'readiness', label: 'Readiness' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'evidence', label: 'Evidence' },
    { key: 'sources', label: 'Sources' },
    { key: 'media', label: 'Media & Rights' },
    { key: 'comparables', label: 'Comparables' },
    { key: 'market', label: 'Market Analysis' },
    { key: 'gaps', label: 'Outstanding Tasks' },
    { key: 'story', label: 'Story Preview' },
    { key: 'studio', label: 'Creative Studio' },
  ];

  // Timeline expand/collapse state (by event index).
  public expandedEvents = new Set<number>();

  public toastMessage = '';
  private toastTimer: any = null;
  private routeSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    private intel: EditorialIntelligenceService
  ) {}

  ngOnInit(): void {
    // Admin pages self-verify (no route guards in this app).
    this.adminService.verify().subscribe(
      (data: { admin: boolean }) => {
        if (!data || !data.admin) {
          this.router.navigate(['/landing']);
          return;
        }
        this.verified = true;
        this.watchRoute();
      },
      () => this.router.navigate(['/landing'])
    );
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  private watchRoute(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const param = params.get('registration');
      const reg = param ? this.intel.normalize(param) : this.intel.defaultRegistration;
      this.load(reg);
    });
  }

  private load(registration: string): void {
    this.registration = registration;
    this.loading = true;
    this.notAvailable = false;
    this.dossier = null;
    this.dataSource = null;

    this.intel.getDossier(registration).subscribe(
      (result) => {
        this.loading = false;
        if (!result) {
          this.notAvailable = true;
          return;
        }
        this.dossier = result.dossier;
        this.dataSource = result.source;
        this.activeTab = 'summary';
      },
      () => {
        this.loading = false;
        this.notAvailable = true;
      }
    );
  }

  public selectTab(key: string): void {
    this.activeTab = key;
  }

  public goToRegistration(reg: string): void {
    this.router.navigate(['/admin/editorial-intelligence', this.intel.normalize(reg)]);
  }

  public get availableRegistrations(): string[] {
    return this.intel.availableRegistrations();
  }

  // ---- Plate Viewer deep-link -------------------------------------------------

  public get plateViewerLink(): any[] {
    return ['/plate-viewer', 'plate', this.registration];
  }

  // ---- Readiness helpers ------------------------------------------------------

  public band(value: number): 'ok' | 'warn' | 'bad' {
    if (value >= 70) return 'ok';
    if (value >= 40) return 'warn';
    return 'bad';
  }

  /** Ten segment cells; the first N reflect the value. */
  public segments(value: number): boolean[] {
    const filled = Math.round(value / 10);
    return Array.from({ length: 10 }, (_, i) => i < filled);
  }

  public overallReadiness(): number {
    const r = this.dossier?.readiness || [];
    if (!r.length) return 0;
    return Math.round(r.reduce((s, c: ReadinessCategory) => s + c.value, 0) / r.length);
  }

  // ---- Tier / usage / licence presentation -----------------------------------

  public tierLabel(tier?: number): string {
    return tier ? `T${tier}` : '—';
  }

  public tierClass(tier?: number): string {
    switch (tier) {
      case 1:
      case 2:
        return 'ok';
      case 3:
        return 'info';
      case 4:
        return 'warn';
      default:
        return 'bad';
    }
  }

  public usageLabel(policy?: string): string {
    switch (policy) {
      case 'publishable_fact':
        return 'Publishable fact';
      case 'publishable_context':
        return 'Publishable context';
      case 'internal_lead':
        return 'Internal lead';
      case 'triage_only':
        return 'Triage only';
      default:
        return policy || '—';
    }
  }

  public usageClass(policy?: string): string {
    switch (policy) {
      case 'publishable_fact':
        return 'ok';
      case 'publishable_context':
        return 'info';
      case 'internal_lead':
        return 'warn';
      default:
        return 'bad';
    }
  }

  public licenceClass(status?: string): string {
    switch (status) {
      case 'owned':
      case 'licensed':
        return 'ok';
      case 'embed_allowed':
        return 'info';
      case 'permission_required':
        return 'warn';
      case 'do_not_use':
        return 'bad';
      default:
        return 'muted'; // reference_only / unknown
    }
  }

  public confClass(label?: string): string {
    switch ((label || '').toLowerCase()) {
      case 'high':
        return 'ok';
      case 'medium':
        return 'info';
      case 'low':
        return 'warn';
      default:
        return 'bad';
    }
  }

  public severityClass(severity?: string): string {
    switch (severity) {
      case 'high':
        return 'bad';
      case 'medium':
        return 'warn';
      default:
        return 'info';
    }
  }

  public provenanceClass(p?: string): string {
    switch (p) {
      case 'authentic':
        return 'authentic';
      case 'render':
        return 'owned';
      case 'concept':
        return 'concept';
      default:
        return 'muted';
    }
  }

  public provenanceLabel(p?: string): string {
    switch (p) {
      case 'authentic':
        return 'Authentic media';
      case 'render':
        return 'PlateX render';
      case 'concept':
        return 'AI concept';
      default:
        return 'Empty slot';
    }
  }

  public statusClass(status?: string): string {
    switch (status) {
      case 'approved':
        return 'ok';
      case 'rejected':
        return 'bad';
      case 'in_review':
      case 'changes_requested':
      case 're_research':
        return 'warn';
      default:
        return 'muted';
    }
  }

  // ---- Evidence helpers -------------------------------------------------------

  public sourceById(id: number): IntelSource | undefined {
    return this.dossier?.sources.find((s) => s.id === id);
  }

  public displayableMediaCount(): number {
    return (this.dossier?.media || []).filter(
      (m) => m.approvedForDisplay && m.displayAllowed
    ).length;
  }

  // ---- Media Discovery (v3) ---------------------------------------------------

  /** True while a discovery run is in flight (disables the button). */
  public discovering = false;
  /** Media ids with an action request in flight. */
  public mediaActionPending = new Set<string>();
  /** Card whose inline note editor is open, and the draft text. */
  public noteEditorId: string | null = null;
  public noteDraft = '';

  private mediaKey(m: IntelMedia): string {
    return String(m.id ?? '');
  }

  /** Run server-side Media Discovery, then refresh the dossier on success. */
  public discoverMedia(): void {
    if (this.discovering || !this.registration) return;
    this.discovering = true;
    this.toast('Discovering media…');
    this.intel.discoverMedia(this.registration).subscribe(
      (res) => {
        this.discovering = false;
        if (res.status === 'completed') {
          const kept = res.candidatesKept ?? 0;
          this.toast(`Discovery complete — ${kept} candidate${kept === 1 ? '' : 's'} ready for review.`);
          this.refreshDossier();
        } else if (res.status === 'egress_unavailable') {
          this.toast(res.detail || 'Media discovery could not reach the internet from this environment.');
        } else {
          this.toast(res.detail || 'No dossier to discover media for.');
        }
      },
      () => {
        this.discovering = false;
        this.toast('Media discovery failed.');
      }
    );
  }

  /** Re-fetch the dossier in place (keeps the user on the Media tab). */
  private refreshDossier(): void {
    this.intel.getDossier(this.registration).subscribe((result) => {
      if (result) {
        this.dossier = result.dossier;
        this.dataSource = result.source;
      }
    });
  }

  public mediaStatusClass(status?: string): string {
    switch (status) {
      case 'approved':
        return 'ok';
      case 'rejected':
        return 'bad';
      case 'flagged':
        return 'warn';
      default:
        return 'muted'; // pending
    }
  }

  /** Relevance as an integer percentage for display, or null if unscored. */
  public relevancePct(m: IntelMedia): number | null {
    if (m.relevanceScore == null) return null;
    return Math.round(m.relevanceScore * 100);
  }

  public isMediaPending(m: IntelMedia): boolean {
    return this.mediaActionPending.has(this.mediaKey(m));
  }

  private applyMediaAction(
    m: IntelMedia,
    action: 'approve' | 'reject' | 'flag' | 'note',
    notes?: string
  ): void {
    const id = m.id;
    if (id == null) {
      this.toast('This media item has no id yet — run discovery first.');
      return;
    }
    const key = this.mediaKey(m);
    this.mediaActionPending.add(key);
    this.intel.mediaAction(id, action, notes).subscribe(
      (ok) => {
        this.mediaActionPending.delete(key);
        if (!ok) {
          this.toast('Action failed — no changes were made.');
          return;
        }
        // Optimistic local update so the card reflects the decision immediately.
        if (action === 'approve') {
          m.status = 'approved';
          m.approvedForDisplay = true;
        } else if (action === 'reject') {
          m.status = 'rejected';
          m.approvedForDisplay = false;
        } else if (action === 'flag') {
          m.status = 'flagged';
        } else if (action === 'note') {
          m.editorNotes = notes ?? m.editorNotes;
        }
        this.toast('Saved.');
      },
      () => {
        this.mediaActionPending.delete(key);
        this.toast('Action failed — no changes were made.');
      }
    );
  }

  public approveMedia(m: IntelMedia): void {
    this.applyMediaAction(m, 'approve');
  }

  public rejectMedia(m: IntelMedia): void {
    this.applyMediaAction(m, 'reject');
  }

  public flagMedia(m: IntelMedia): void {
    this.applyMediaAction(m, 'flag');
  }

  public startNote(m: IntelMedia): void {
    this.noteEditorId = this.mediaKey(m);
    this.noteDraft = m.editorNotes || '';
  }

  public cancelNote(): void {
    this.noteEditorId = null;
    this.noteDraft = '';
  }

  public isNoteOpen(m: IntelMedia): boolean {
    return this.noteEditorId === this.mediaKey(m);
  }

  public saveNote(m: IntelMedia): void {
    const note = (this.noteDraft || '').trim();
    this.applyMediaAction(m, 'note', note);
    this.cancelNote();
  }

  public piiSourceCount(): number {
    return (this.dossier?.sources || []).filter((s) => s.piiPresent).length;
  }

  // ---- V2: Editorial Decision -------------------------------------------------

  public recommendationLabel(rec?: string): string {
    switch (rec) {
      case 'ready':
        return 'Ready to publish';
      case 'ready_conditions':
        return 'Ready with conditions';
      case 'research_required':
        return 'Research required';
      case 'not_suitable':
        return 'Not suitable';
      default:
        return '—';
    }
  }

  public recommendationClass(rec?: string): string {
    switch (rec) {
      case 'ready':
        return 'ok';
      case 'ready_conditions':
        return 'warn';
      case 'research_required':
        return 'info';
      case 'not_suitable':
        return 'bad';
      default:
        return 'muted';
    }
  }

  /** Overall story score: explicit override, else the mean of the metrics. */
  public storyScore(decision?: EditorialDecision): number {
    if (!decision) return 0;
    if (decision.storyScore.overrideOverall != null) {
      return Math.round(decision.storyScore.overrideOverall);
    }
    const m = decision.storyScore.metrics || [];
    if (!m.length) return 0;
    return Math.round(m.reduce((s, x) => s + x.value, 0) / m.length);
  }

  public scoreBand(value: number): 'ok' | 'warn' | 'bad' {
    return this.band(value);
  }

  // ---- V2: readiness facets ---------------------------------------------------

  public stateClass(state?: string): string {
    switch (state) {
      case 'complete':
        return 'ok';
      case 'partial':
        return 'warn';
      default:
        return 'bad'; // outstanding
    }
  }

  public stateLabel(state?: string): string {
    switch (state) {
      case 'complete':
        return 'Complete';
      case 'partial':
        return 'Partial';
      case 'outstanding':
        return 'Outstanding';
      default:
        return '—';
    }
  }

  // ---- V2: next actions -------------------------------------------------------

  public runAction(action: NextAction): void {
    if (action.enabled && action.link) {
      this.router.navigate(action.link);
      return;
    }
    this.toast(action.label + ' — prototype action (not wired yet).');
  }

  // ---- V2: timeline expand / linked evidence ---------------------------------

  public toggleEvent(index: number): void {
    if (this.expandedEvents.has(index)) this.expandedEvents.delete(index);
    else this.expandedEvents.add(index);
  }

  public isExpanded(index: number): boolean {
    return this.expandedEvents.has(index);
  }

  /** Claims whose sources overlap this event's sources = linked evidence. */
  public linkedEvidence(event: IntelTimelineEvent): IntelClaim[] {
    const ids = event.sourceIds || [];
    if (!ids.length || !this.dossier) return [];
    return this.dossier.claims.filter((c) =>
      (c.sourceIds || []).some((s) => ids.includes(s))
    );
  }

  // ---- V2: evidence purpose badges -------------------------------------------

  public purposeLabel(p: string): string {
    return 'Supports ' + p;
  }

  // ---- V2: source enrichment (placeholders where data is unavailable) --------

  public discoveryFor(s: IntelSource): string {
    if (s.discoveryMethod) return s.discoveryMethod;
    switch (s.accessMethod) {
      case 'manual':
        return 'Internal export';
      case 'direct_fetch':
        return 'Direct fetch';
      case 'search_index':
        return 'Web search';
      case 'api':
        return 'API';
      default:
        return 'Not recorded';
    }
  }

  public lastVerifiedFor(s: IntelSource): string {
    return s.lastVerified || s.dateAccessed || 'Not verified';
  }

  public licenceFor(s: IntelSource): string {
    if (s.licence) return s.licence;
    if (s.tier === 1) return 'Owned (internal)';
    if (s.tier === 2) return 'Editorial reference';
    if (s.tier === 3) return 'Cite & link';
    return 'Reference only';
  }

  public archivedLabel(s: IntelSource): string {
    return s.archived ? 'Archived' : 'Not archived';
  }

  // ---- V2: outstanding tasks --------------------------------------------------

  public priorityClass(priority?: string): string {
    switch (priority) {
      case 'high':
        return 'bad';
      case 'medium':
        return 'warn';
      default:
        return 'info';
    }
  }

  public openTaskCount(): number {
    return (this.dossier?.outstandingTasks || []).filter((t) => !t.completed).length;
  }

  /** Local-only prototype toggle (no backend write). */
  public toggleTask(task: OutstandingTask): void {
    task.completed = !task.completed;
    this.toast(
      task.completed ? 'Marked done (prototype).' : 'Reopened (prototype).'
    );
  }

  // ---- Prototype-only affordances (no generation / no writes) -----------------

  public toast(message: string): void {
    this.toastMessage = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toastMessage = ''), 3200);
  }
}
